import type { RowData } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "../Button";
import { Select, SelectItem } from "../Select";
import type { Table } from "./features";

interface PaginationProps<TData extends RowData> {
  table: Table<TData>;
}

const pageSizeOptions = [10, 25, 50];

export function DataTablePagination<TData extends RowData>({ table }: Readonly<PaginationProps<TData>>) {
  return (
    <div className="flex shrink-0 items-center justify-between border-t border-medium-gray bg-white px-2 py-1">
      <div className="ml-auto flex items-center gap-x-6 lg:gap-x-8">
        <div className="flex items-center gap-x-2">
          <p className="text-small font-semibold">Rows per page</p>
          <Select
            value={`${table.state.pagination.pageSize}`}
            onValueChange={(value) => table.setPageSize(Number(value))}
            placeholder={table.state.pagination.pageSize}
            className="[&>button]:px-2 [&>button]:py-0.5"
          >
            {pageSizeOptions.map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`}>
                {pageSize}
              </SelectItem>
            ))}
          </Select>
        </div>
        <div className="flex w-16 items-center justify-center text-small font-semibold">
          Page {table.state.pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center gap-x-2">
          <Button
            variant="outlined"
            className="hidden size-6 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outlined"
            className="size-6 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outlined"
            className="size-6 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outlined"
            className="hidden size-6 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
